import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, Mail, Crown, Store, CheckCircle2, XCircle, Loader2, Inbox,
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function ShopInbox() {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, xRes] = await Promise.all([
        api.get("/me/invitations").catch(() => ({ data: { items: [] } })),
        api.get("/me/ownership-transfers").catch(() => ({ data: { items: [] } })),
      ]);
      setInvitations(iRes.data.items || []);
      setTransfers(xRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load inbox");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const accept = async (inv) => {
    setBusyId(inv.invitation_id);
    try {
      await api.post(`/invitations/${inv.invitation_id}/accept`);
      toast.success(`Joined ${inv.shop?.name || "the team"}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't accept");
    } finally { setBusyId(null); }
  };
  const decline = async (inv) => {
    if (!window.confirm("Decline this invitation?")) return;
    setBusyId(inv.invitation_id);
    try {
      await api.post(`/invitations/${inv.invitation_id}/decline`);
      toast.success("Declined.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't decline");
    } finally { setBusyId(null); }
  };

  const acceptTransfer = async (t) => {
    if (!window.confirm(`Accept ownership of ${t.shop?.name || "this business"}? This is a permanent action once completed.`)) return;
    setBusyId(t.transfer_id);
    try {
      await api.post(`/ownership-transfers/${t.transfer_id}/accept`);
      toast.success(t.require_admin_approval ? "Accepted. Awaiting Hidden Gem admin approval to finalize." : "Ownership transferred to you.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't accept");
    } finally { setBusyId(null); }
  };
  const declineTransfer = async (t) => {
    if (!window.confirm("Decline this ownership offer?")) return;
    setBusyId(t.transfer_id);
    try {
      await api.post(`/ownership-transfers/${t.transfer_id}/decline`);
      toast.success("Declined.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't decline");
    } finally { setBusyId(null); }
  };

  const pendingTransfers = transfers.filter((t) => t.status === "pending_target");
  const historyTransfers = transfers.filter((t) => t.status !== "pending_target");

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24 space-y-4" data-testid="shop-inbox-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="inbox-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-zinc-950 to-pink-500/10 p-5">
        <p className="inline-flex items-center gap-2 text-cyan-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <Inbox className="w-3 h-3" /> Shop Inbox
        </p>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Team & ownership invitations</h1>
        <p className="text-sm text-zinc-400 mt-1">Accept invitations to join a shop team, or review offers to take over ownership.</p>
      </header>

      {loading ? (
        <div className="py-6 text-center text-zinc-500 inline-flex items-center gap-2 justify-center w-full text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          {/* Ownership transfers */}
          {pendingTransfers.length > 0 && (
            <section className="rounded-3xl bg-white/[0.03] border border-rose-400/40 p-5" data-testid="inbox-ownership-transfers">
              <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2 text-rose-100">
                <Crown className="w-4 h-4" /> Ownership offers
              </h2>
              <div className="mt-3 space-y-2">
                {pendingTransfers.map((t) => (
                  <div key={t.transfer_id} className="rounded-2xl border border-rose-400/40 bg-rose-500/5 p-4" data-testid={`inbox-transfer-${t.transfer_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl overflow-hidden bg-white/10 border border-white/10">
                        {t.shop?.logo_url ? <img src={fileUrl(t.shop.logo_url)} alt={t.shop?.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><Store className="w-5 h-5 text-zinc-400" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-black">{t.shop?.name || "Hidden Gem shop"}</p>
                        <p className="text-[11px] text-zinc-400">From {t.initiator_email} · {new Date(t.created_at).toLocaleDateString()}</p>
                        {t.reason && <p className="text-xs text-rose-100 mt-1"><b>Reason:</b> {t.reason}</p>}
                        {t.require_admin_approval && <p className="text-[10px] text-amber-200 mt-1">Requires admin approval to finalize.</p>}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button
                        onClick={() => acceptTransfer(t)}
                        disabled={busyId === t.transfer_id}
                        className="flex-1 h-10 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold"
                        data-testid={`inbox-transfer-accept-${t.transfer_id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept ownership
                      </Button>
                      <Button
                        onClick={() => declineTransfer(t)}
                        disabled={busyId === t.transfer_id}
                        variant="outline"
                        className="flex-1 h-10 rounded-full bg-white/5 hover:bg-white/10 border-white/10 text-white text-sm font-bold"
                        data-testid={`inbox-transfer-decline-${t.transfer_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1.5" /> Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Team invitations */}
          <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="inbox-team-invitations">
            <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2">
              <Mail className="w-4 h-4" /> Team invitations ({invitations.length})
            </h2>
            {invitations.length === 0 ? (
              <p className="text-sm text-zinc-500 mt-2" data-testid="inbox-invitations-empty">No pending invitations.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {invitations.map((inv) => (
                  <div key={inv.invitation_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={`inbox-invitation-${inv.invitation_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl overflow-hidden bg-white/10 border border-white/10">
                        {inv.shop?.logo_url ? <img src={fileUrl(inv.shop.logo_url)} alt={inv.shop?.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><Store className="w-5 h-5 text-zinc-400" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-black">{inv.shop?.name || "Hidden Gem shop"}</p>
                        <p className="text-[11px] text-zinc-400 capitalize">Role: {inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button
                        onClick={() => accept(inv)}
                        disabled={busyId === inv.invitation_id}
                        className="flex-1 h-10 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-bold"
                        data-testid={`inbox-invitation-accept-${inv.invitation_id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept
                      </Button>
                      <Button
                        onClick={() => decline(inv)}
                        disabled={busyId === inv.invitation_id}
                        variant="outline"
                        className="flex-1 h-10 rounded-full bg-white/5 hover:bg-white/10 border-white/10 text-white text-sm font-bold"
                        data-testid={`inbox-invitation-decline-${inv.invitation_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1.5" /> Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {historyTransfers.length > 0 && (
            <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="inbox-transfer-history">
              <h2 className="text-lg font-black tracking-tight">Ownership history</h2>
              <div className="mt-3 space-y-1">
                {historyTransfers.map((t) => (
                  <div key={t.transfer_id} className="text-xs text-zinc-400 flex items-center gap-2">
                    <span className="capitalize">{t.status.replace("_", " ")}</span>
                    <span>· {t.shop?.name || t.business_id}</span>
                    <span className="ml-auto">{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
